import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

/**
 * Nestly brand wordmark (mark lives only in favicon / SVG assets).
 * Single text node "Nestly" so metallic shimmer (background-clip:text) sweeps
 * continuously across letter glyphs only. Playfair italic; sage slash accent
 * lives on the lockup (outside the clipped text). Hover shine yoyos while
 * hovered (`is-shimmering`); mouseleave removes the class to stop the loop.
 * Under prefers-reduced-motion: solid wordmark, no load/hover shimmer.
 */
export default function Logo({
  className,
  wordmarkClassName,
}) {
  const shimmerRef = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduceMotion(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const startShineLoop = useCallback(() => {
    if (reduceMotion) return;
    const el = shimmerRef.current;
    if (!el) return;
    el.classList.remove("is-shimmering");
    // Force style recalc so a new animation always starts on hover.
    void el.offsetWidth;
    el.classList.add("is-shimmering");
  }, [reduceMotion]);

  const stopShineLoop = useCallback(() => {
    const el = shimmerRef.current;
    if (!el) return;
    el.classList.remove("is-shimmering");
  }, []);

  useEffect(() => {
    const el = shimmerRef.current;
    if (!el || reduceMotion) return undefined;
    el.classList.add("brand-shimmer--boot");
    const onEnd = (event) => {
      if (event.target !== el) return;
      if (event.animationName === "brand-shimmer-load") {
        el.classList.remove("brand-shimmer--boot");
      }
    };
    el.addEventListener("animationend", onEnd);
    return () => el.removeEventListener("animationend", onEnd);
  }, [reduceMotion]);

  return (
    <span
      className={cn(
        "group/logo inline-flex items-center text-stone2-dark",
        className
      )}
      aria-label="Nestly"
      onMouseEnter={startShineLoop}
      onMouseLeave={stopShineLoop}
    >
      <span className="brand-lockup brand-lockup--nestly">
        <span
          ref={shimmerRef}
          className={cn(
            "brand-shimmer leading-none tracking-tight",
            reduceMotion && "brand-shimmer--static",
            wordmarkClassName
          )}
          aria-hidden="true"
        >
          Nestly
        </span>
      </span>
    </span>
  );
}
